/**
 * Returns a date formatted as "YYYY-MM-DD" in the Europe/Amsterdam timezone.
 * Use this instead of new Date() / moment() for "today" so that users in other
 * timezones (e.g. USA) see the same calendar date as the NL server.
 */
export function getNLDateString(date: Date = new Date()): string {
    // 'en-CA' locale produces the ISO YYYY-MM-DD format natively.
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Amsterdam' }).format(date);
}

/**
 * Parses a "YYYY-MM-DD" string (as returned by the API) into a local Date at noon.
 *
 * Why noon?  `new Date("2026-06-15")` is parsed as UTC *midnight*, which in timezones
 * west of UTC (e.g. US East = UTC-5) becomes June 14 at 19:00 local time — causing
 * getDate() / getDay() / getMonth() to return the wrong day.  Creating the date at
 * local noon guarantees that local calendar methods always return the intended date
 * regardless of the user's UTC offset (covers UTC-11 … UTC+12).
 */
export function parseAPIDate(dateString: string): Date {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day, 12, 0, 0);
}

/** Formats a Date back to the "YYYY-MM-DD" string the API expects, using local calendar fields. */
export function toAPIDateString(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

/** Returns the Monday of the ISO week containing `date` (local calendar day, time-zone safe). */
export function mondayOfWeek(date: Date): Date {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0);
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diff);
    return d;
}

/** Adds `days` (may be negative) to an API date string and returns the new API date string. */
export function addDaysToAPIDate(dateString: string, days: number): string {
    const d = parseAPIDate(dateString);
    d.setDate(d.getDate() + days);
    return toAPIDateString(d);
}

/** ISO-8601 week number (1-53) for an API date string. */
export function getIsoWeekNumber(dateString: string): number {
    const d = parseAPIDate(dateString);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 4 - (d.getDay() || 7));
    const yearStart = new Date(d.getFullYear(), 0, 1);
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

